/**
 * National Driving Guild — PDI-focused landing page.
 *
 * Audience: PDIs (trainee instructors) preparing for Part 1, 2, 3 + ADI standards.
 * Headline promise: career path with structured prep, real mentors, and a verified pathway to ADI.
 *
 * Visual language matches the main Landing — premium black/gold/white.
 */
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import CoreJourneyMantra from "../components/CoreJourneyMantra";
import { useReveal } from "../hooks/useReveal";
import { useSectionEngagement } from "../hooks/useSectionEngagement";
import { trackCta } from "../utils/analytics";
import { AudienceSwitcher } from "./LandingLearner";
import {
  ArrowRight, Sparkles, Star, BookOpen, Award, Compass, ShieldCheck,
  Quote, Calendar, ChevronRight, Zap, GraduationCap, Trophy, Lock,
  HeartHandshake, BadgeCheck, Users, Target, MessagesSquare, Eye,
  Brain, Rocket,
} from "lucide-react";

/* Shared NDG brand shield. */
function NdgShield({ size = 42 }) {
  return (
    <svg width={size} height={size * 1.1} viewBox="0 0 40 44" aria-hidden className="flex-shrink-0">
      <defs>
        <linearGradient id="ndg-gold-pdi" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F3E5AB" />
          <stop offset="0.5" stopColor="#D4AF37" />
          <stop offset="1" stopColor="#A37B1F" />
        </linearGradient>
      </defs>
      <path d="M20 1 L37 6 V20 C37 30 30 38 20 42 C10 38 3 30 3 20 V6 Z" fill="#0a0a0a" stroke="url(#ndg-gold-pdi)" strokeWidth="1.4" />
      <text x="20" y="18" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="900" fontSize="9" fill="url(#ndg-gold-pdi)" letterSpacing="0.5">NDG</text>
      <g fill="url(#ndg-gold-pdi)">
        <polygon points="11,28 12,30.5 14.5,30.5 12.5,32 13.3,34.5 11,33 8.7,34.5 9.5,32 7.5,30.5 10,30.5" />
        <polygon points="20,28 21,30.5 23.5,30.5 21.5,32 22.3,34.5 20,33 17.7,34.5 18.5,32 16.5,30.5 19,30.5" />
        <polygon points="29,28 30,30.5 32.5,30.5 30.5,32 31.3,34.5 29,33 26.7,34.5 27.5,32 25.5,30.5 28,30.5" />
      </g>
    </svg>
  );
}

function Reveal({ children, delay = 0 }) {
  const [ref, on] = useReveal();
  return (
    <div ref={ref} className={`reveal ${on ? "reveal-in" : ""}`} style={delay ? { transitionDelay: `${delay}ms` } : undefined}>
      {children}
    </div>
  );
}

export default function LandingPDI() {
  const { user } = useAuth();
  const primaryHref = user ? "/dashboard" : "/register?role=pdi";

  return (
    <div className="min-h-dvh no-overflow-x bg-[#050505] text-white font-outfit antialiased overflow-x-hidden" data-testid="landing-pdi">
      <div className="fixed inset-0 grain-overlay opacity-[0.05] pointer-events-none z-0" aria-hidden />
      <Header user={user} primaryHref={primaryHref} />
      <main className="relative">
        <Hero primaryHref={primaryHref} />
        <Reveal><AudienceSwitcher current="pdi" /></Reveal>
        <Reveal><PDIPain /></Reveal>
        <Reveal><WhyPDI /></Reveal>
        <Reveal><PathToADI /></Reveal>
        <Reveal><PDITestimonial /></Reveal>
        <Reveal><FinalCta primaryHref={primaryHref} /></Reveal>
        <Reveal><TrustFooter /></Reveal>
      </main>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* HEADER                                                              */
/* ─────────────────────────────────────────────────────────────────── */

function Header({ user, primaryHref }) {
  return (
    <header className="sticky top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#050505]/80 border-b border-white/[0.07]">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 h-[72px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
          <NdgShield size={42} />
          <div className="hidden sm:block leading-tight">
            <div className="font-condensed text-white text-lg md:text-xl tracking-wider">
              NATIONAL<br className="md:hidden" /> <span className="md:inline">DRIVING GUILD</span>
            </div>
            <div className="text-[8.5px] md:text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-medium">
              For PDIs · Part 1 to qualified ADI
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 text-sm text-zinc-300">
          <Link to="/for-learners" data-testid="nav-learners" className="hover:text-white transition">For Learners</Link>
          <Link to="/" data-testid="nav-instructors" className="hover:text-white transition">For Instructors</Link>
          <Link to="/for-pdis" data-testid="nav-pdis-active" className="text-white border-b-2 border-[#D4AF37] pb-0.5">For PDIs</Link>
          <Link to="/pricing?audience=pdi" data-testid="nav-pricing" className="hover:text-white transition">Pricing</Link>
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <Link to="/dashboard">
              <Button data-testid="go-to-dashboard-btn" className="bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] font-bold rounded-md px-4 md:px-5 h-10 text-sm guild-cta-glow">
                Dashboard
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <Button data-testid="login-btn" variant="outline" className="bg-transparent border-white/25 text-white hover:bg-white/5 font-semibold rounded-md h-10 px-4 md:px-5 text-sm">
                  Log In
                </Button>
              </Link>
              <Link to={primaryHref}>
                <Button data-testid="signup-btn" className="bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] font-bold rounded-md h-10 px-4 md:px-5 text-sm guild-cta-glow">
                  Join The Guild
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* HERO                                                                */
/* ─────────────────────────────────────────────────────────────────── */

const HERO_BULLETS = [
  { icon: BookOpen, title: "Part 1 study modules", body: "Theory, hazard perception, with progress tracking." },
  { icon: Brain, title: "Part 2 prep", body: "Drive analysis, eco/safe driving feedback." },
  { icon: Award, title: "Part 3 / Standards Check", body: "Mock Part 3 simulator and instructor lesson rubrics." },
  { icon: Users, title: "Find a Trainer", body: "Browse verified ADI trainers with PDI specialisms." },
  { icon: MessagesSquare, title: "PDI Community", body: "Moderated peer space — no nonsense, no toxic noise." },
  { icon: BadgeCheck, title: "Path-to-ADI tracking", body: "Visible milestones from PDI to qualified ADI." },
];

function Hero({ primaryHref }) {
  const [sectionRef, markCtaClicked] = useSectionEngagement("pdi_hero");
  return (
    <section ref={sectionRef} data-testid="pdi-hero-section" className="relative pt-12 md:pt-14 pb-12 md:pb-16">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 relative z-10">
          <div data-testid="pdi-hero-eyebrow" className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/35 bg-[#D4AF37]/[0.07] px-3 py-1 text-[10.5px] uppercase tracking-[0.24em] text-[#F3E5AB] font-semibold mb-6">
            <GraduationCap className="w-3 h-3" /> For PDIs
          </div>

          <h1 data-testid="pdi-hero-title" className="font-display font-semibold text-white tracking-tight leading-[1.02]" style={{ fontSize: "clamp(2.4rem, 5.4vw, 4.5rem)" }}>
            Become an instructor<br />
            <span className="text-gold-gradient">with confidence.</span>
          </h1>

          <CoreJourneyMantra className="mt-5" testid="pdi-hero-mantra" />

          <p data-testid="pdi-hero-subheadline" className="mt-6 md:mt-7 text-base md:text-lg text-zinc-300 leading-relaxed max-w-xl">
            From Part 1 prep through Standards Check — National Driving Guild gives PDIs structured study, mock simulators, real trainers, and a clear path to qualified ADI status.
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link to={primaryHref} onClick={() => { markCtaClicked({ cta: "hero_join" }); trackCta("pdi_landing_join_cta", "Join the Guild as a PDI", { destination: primaryHref }); }}>
              <Button data-testid="pdi-hero-join-btn" className="h-13 px-6 rounded-md bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] text-[14px] font-bold guild-cta-glow w-full sm:w-auto">
                Join the Guild as a PDI <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing?audience=pdi" onClick={() => { markCtaClicked({ cta: "hero_pricing" }); trackCta("pdi_landing_pricing_cta", "See pricing", { destination: "/pricing?audience=pdi" }); }}>
              <Button data-testid="pdi-hero-pricing-btn" variant="outline" className="h-13 px-6 rounded-md bg-transparent border border-[#D4AF37]/45 hover:bg-[#D4AF37]/[0.06] text-white text-[14px] font-semibold w-full sm:w-auto">
                See pricing
              </Button>
            </Link>
          </div>

          <p data-testid="pdi-hero-trust-line" className="mt-5 text-[13px] text-zinc-400 inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span>
              <span className="text-white font-semibold">Mock Part 3 simulator.</span> Real trainers. Real feedback.
            </span>
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-3.5">
            {HERO_BULLETS.map(({ icon: Icon, title, body }, i) => (
              <div key={i} data-testid={`pdi-hero-bullet-${i}`} className="flex items-start gap-3 rounded-xl bg-white/[0.025] border border-white/[0.06] p-3.5">
                <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-[13.5px] font-semibold text-white leading-tight">{title}</div>
                  <div className="text-[12px] text-zinc-400 mt-0.5 leading-snug">{body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — Path-to-ADI mock card */}
        <div className="lg:col-span-5 relative">
          <div className="relative rounded-3xl bg-gradient-to-br from-[#16120a] via-[#0c0c0c] to-[#0a0a0a] border border-[#D4AF37]/30 guild-card-glow p-6 md:p-7 overflow-hidden">
            <div aria-hidden className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle at center, #D4AF37 0%, transparent 70%)" }} />

            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold flex items-center gap-2 mb-5">
              <span className="w-6 h-px bg-[#D4AF37]/60" /> Your PDI tracker
            </div>

            <div className="space-y-3">
              {[
                { stage: "Part 1 — Theory & HPT", pct: 100, status: "Passed", color: "emerald" },
                { stage: "Part 2 — Driving Ability", pct: 70, status: "70% mock score", color: "gold" },
                { stage: "Part 3 — Instructional Ability", pct: 35, status: "3 mocks complete", color: "gold" },
                { stage: "Qualified ADI", pct: 0, status: "Locked — clear Part 3", color: "muted" },
              ].map((s, i) => (
                <div key={i} className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-3.5">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="text-[13px] font-semibold text-white">{s.stage}</div>
                    <span className={`text-[10.5px] font-bold rounded-full px-2 py-0.5 ${
                      s.color === "emerald"
                        ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                        : s.color === "gold"
                        ? "bg-[#D4AF37]/15 text-[#F3E5AB] border border-[#D4AF37]/35"
                        : "bg-white/[0.04] text-zinc-500 border border-white/10"
                    }`}>
                      {s.status}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        s.color === "emerald" ? "bg-emerald-400" : s.color === "gold" ? "bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37]" : "bg-white/10"
                      }`}
                      style={{ width: `${s.pct}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <Link to={primaryHref} className="mt-5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#F3E5AB] hover:text-white transition">
              Start your tracker <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* PDI PAIN POINTS                                                     */
/* ─────────────────────────────────────────────────────────────────── */

const PDI_PAINS = [
  "Studying alone with random YouTube tutorials",
  "No real feedback on my mock lessons",
  "Trainer kept cancelling — I lost weeks",
  "Part 3 came too fast and I wasn't ready",
  "Spent £££ on resits that could've been avoided",
  "Felt isolated — no one to ask 'is this normal?'",
];

function PDIPain() {
  const [sectionRef] = useSectionEngagement("pdi_pain");
  return (
    <section ref={sectionRef} data-testid="pdi-pain-section" className="relative py-12 md:py-16">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#D4AF37] font-bold mb-3">PDI reality</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white leading-tight">
            The PDI journey<br />is brutal alone.
          </h2>
          <p className="mt-5 text-[15px] text-zinc-300 leading-relaxed max-w-md">
            Most PDIs drop out before qualifying — not because they aren't capable, but because the system doesn't support them. NDG fixes that.
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="grid sm:grid-cols-2 gap-3">
            {PDI_PAINS.map((w, i) => (
              <div key={i} data-testid={`pdi-pain-${i}`} className="rounded-xl border border-white/[0.06] bg-white/[0.025] p-4 flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-rose-500/10 border border-rose-500/30 flex items-center justify-center flex-shrink-0">
                  <Quote className="w-3.5 h-3.5 text-rose-300" />
                </div>
                <div className="text-[13.5px] text-zinc-200 leading-snug">"{w}"</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* WHY PDI                                                             */
/* ─────────────────────────────────────────────────────────────────── */

const PDI_FEATURES = [
  { icon: BookOpen, title: "Part 1, 2, 3 modules", body: "Structured curriculum across all parts with progress tracking and confidence checks." },
  { icon: Target, title: "Mock Part 3 simulator", body: "Realistic role-play scenarios with rubric-graded feedback against the DVSA Part 3 form." },
  { icon: Users, title: "Find a Trainer", body: "Browse verified ADI trainers tagged with PDI specialisms. Vetted, reviewed, no surprises." },
  { icon: Award, title: "Standards Check practice", body: "Same tools real ADIs use to prep — uploaded lesson footage analysed by AI." },
  { icon: MessagesSquare, title: "PDI Community", body: "A moderated, professional space. Learn from others further down the road. No spammy WhatsApp groups." },
  { icon: ShieldCheck, title: "Verified pathway", body: "Your Path-to-ADI tracker shows trainers and Guild admins exactly where you are." },
];

function WhyPDI() {
  const [sectionRef] = useSectionEngagement("pdi_why");
  return (
    <section ref={sectionRef} data-testid="pdi-why-section" className="relative py-12 md:py-16">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="max-w-2xl mb-10">
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#D4AF37] font-bold mb-3">Why PDIs choose NDG</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white leading-tight">
            The full toolkit,<br />
            <span className="text-gold-gradient">from Part 1 to qualified.</span>
          </h2>
          <p className="mt-4 text-[15px] text-zinc-300 leading-relaxed">
            Built by ADIs who remember what the PDI grind actually feels like.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PDI_FEATURES.map(({ icon: Icon, title, body }, i) => (
            <div key={i} data-testid={`pdi-feature-${i}`} className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-5 hover:border-[#D4AF37]/35 transition">
              <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="font-display text-lg font-semibold text-white">{title}</div>
              <div className="mt-2 text-[13.5px] text-zinc-300 leading-relaxed">{body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* PATH TO ADI                                                         */
/* ─────────────────────────────────────────────────────────────────── */

const PATH = [
  { n: "01", icon: BookOpen, title: "Part 1 — Theory & Hazard Perception", body: "Build a strong theory base, study modules + targeted hazard practice. Track every mock until you're consistently in the top band." },
  { n: "02", icon: Compass, title: "Part 2 — Driving Ability", body: "Drive analysis tools, eco/safe driving rubrics, and trainer feedback. Walk in knowing your weak spots are gone." },
  { n: "03", icon: Trophy, title: "Part 3 — Instructional Ability", body: "Mock Part 3 simulator + rubric-graded scenarios. Verified trainers can review your sessions before the real day." },
  { n: "04", icon: BadgeCheck, title: "Qualified ADI — Standards Check ready", body: "Hit the road with the same tools full ADIs use. Standards Check prep is built in from day one." },
];

function PathToADI() {
  const [sectionRef] = useSectionEngagement("pdi_path");
  return (
    <section ref={sectionRef} data-testid="pdi-path-section" className="relative py-12 md:py-16">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#D4AF37] font-bold mb-3">Path to ADI</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white leading-tight">
            Four stages.<br /><span className="text-gold-gradient">One guided track.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {PATH.map(({ n, icon: Icon, title, body }, i) => (
            <div key={i} data-testid={`pdi-path-${i}`} className="relative rounded-2xl border border-white/[0.07] bg-gradient-to-br from-[#0d0d0d] to-[#080808] p-6">
              <div className="flex items-start gap-4">
                <div className="text-[40px] font-display font-bold text-[#D4AF37]/20 leading-none flex-shrink-0">{n}</div>
                <div>
                  <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div className="font-display text-lg font-semibold text-white">{title}</div>
                  <div className="mt-2 text-[13.5px] text-zinc-300 leading-relaxed">{body}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* TESTIMONIAL                                                         */
/* ─────────────────────────────────────────────────────────────────── */

function PDITestimonial() {
  return (
    <section className="relative py-12 md:py-16">
      <div className="max-w-[900px] mx-auto px-4 md:px-8">
        <div className="rounded-3xl border border-white/[0.07] bg-white/[0.025] p-7 md:p-10">
          <div className="flex items-center gap-1 mb-5">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />)}
          </div>
          <Quote className="w-7 h-7 text-[#D4AF37]/40 mb-3" />
          <p className="font-display text-xl md:text-2xl text-white leading-relaxed">
            "Failed Part 3 twice on my own. Joined NDG, found a real trainer, ran 6 mock Part 3s with proper feedback. Passed third attempt — and I knew I was ready before I walked in."
          </p>
          <div className="mt-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37]/30 to-[#0a0a0a] border border-[#D4AF37]/30 flex items-center justify-center text-[#F3E5AB] font-bold text-sm">DC</div>
            <div>
              <div className="text-sm font-semibold text-white">Daniel C.</div>
              <div className="text-[12px] text-zinc-400">Qualified ADI · Birmingham, Jan 2026</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* FINAL CTA                                                           */
/* ─────────────────────────────────────────────────────────────────── */

function FinalCta({ primaryHref }) {
  const [sectionRef, markCtaClicked] = useSectionEngagement("pdi_final_cta");
  return (
    <section ref={sectionRef} data-testid="pdi-final-cta-section" className="relative py-12 md:py-20">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="relative rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#16120a] via-[#0c0c0c] to-[#0a0a0a] guild-card-glow p-8 md:p-14 overflow-hidden">
          <div aria-hidden className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle at center, #D4AF37 0%, transparent 70%)" }} />
          <div className="relative max-w-2xl">
            <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#D4AF37] font-bold mb-4 inline-flex items-center gap-2">
              <Zap className="w-3 h-3" /> Ready to qualify
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-white leading-tight">
              Stop studying solo.<br />
              <span className="text-gold-gradient">Start with the Guild.</span>
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-zinc-300 leading-relaxed">
              PDI Hub access, Mock Part 3 simulator, trainer marketplace, and a real community — all from day one.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to={primaryHref} onClick={() => { markCtaClicked({ cta: "final_join" }); trackCta("pdi_landing_final_cta", "Join the Guild as a PDI", { destination: primaryHref }); }}>
                <Button data-testid="pdi-final-join-btn" className="h-13 px-7 rounded-md bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] text-[14px] font-bold guild-cta-glow w-full sm:w-auto">
                  Join the Guild as a PDI <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/pricing" onClick={() => { markCtaClicked({ cta: "final_pricing" }); }}>
                <Button data-testid="pdi-final-pricing-btn" variant="outline" className="h-13 px-7 rounded-md bg-transparent border border-[#D4AF37]/45 hover:bg-[#D4AF37]/[0.06] text-white text-[14px] font-semibold w-full sm:w-auto">
                  See pricing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* TRUST FOOTER                                                        */
/* ─────────────────────────────────────────────────────────────────── */

const TRUST_ITEMS = [
  { icon: BadgeCheck, label: "DVSA-Aligned Curriculum" },
  { icon: Lock, label: "Secure & Private" },
  { icon: HeartHandshake, label: "Real Trainers. No Runaround." },
  { icon: Rocket, label: "Built By PDIs Who Made It" },
];

function TrustFooter() {
  return (
    <section className="relative pb-16 md:pb-24">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div data-testid="trust-footer" className="border-t border-white/[0.06] pt-8 grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-4 md:divide-x md:divide-white/[0.06]">
          {TRUST_ITEMS.map(({ icon: Icon, label }, i) => (
            <div key={i} data-testid={`trust-foot-${i}`} className="flex items-center justify-center gap-2.5 text-zinc-300 text-[12.5px] md:text-sm font-medium">
              <Icon className="w-4 h-4 text-[#D4AF37]" />
              <span>{label}</span>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-white/[0.04] flex flex-col md:flex-row gap-4 md:items-center md:justify-between text-[11px] text-zinc-500">
          <div className="flex items-center gap-2.5">
            <NdgShield size={22} />
            <span className="font-condensed text-zinc-300 tracking-wider">NATIONAL DRIVING GUILD</span>
            <span className="hidden md:inline text-zinc-600">·</span>
            <span className="hidden md:inline">A professional home for PDIs and ADIs.</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/terms" className="hover:text-zinc-200 transition">Terms</Link>
            <Link to="/privacy" className="hover:text-zinc-200 transition">Privacy</Link>
            <Link to="/pricing" className="hover:text-zinc-200 transition">Pricing</Link>
          </div>
        </div>
      </div>
    </section>
  );
}
